'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend,  } from 'recharts';
import { MapPin, TrendingUp, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, ArrowLeft, Filter, Download,  } from 'lucide-react';

const COUNTIES = [
  { name: 'Montserrado', capital: 'Monrovia', target: 42.0, collected: 38.4, properties: 28400, compliant: 72, delinquent: 3240, delinquentAmt: 18.2, trend: [28, 31, 34, 36, 38, 38.4], topType: 'Commercial/Industrial', q1: 18.2, q2: 20.2 },
  { name: 'Margibi', capital: 'Kakata', target: 8.4, collected: 6.9, properties: 8200, compliant: 58, delinquent: 980, delinquentAmt: 4.1, trend: [4.2, 4.8, 5.4, 5.9, 6.4, 6.9], topType: 'Residential', q1: 3.1, q2: 3.8 },
  { name: 'Bong', capital: 'Gbarnga', target: 5.2, collected: 3.8, properties: 6100, compliant: 49, delinquent: 820, delinquentAmt: 2.8, trend: [1.8, 2.1, 2.6, 3.0, 3.4, 3.8], topType: 'Farmland', q1: 1.6, q2: 2.2 },
  { name: 'Nimba', capital: 'Sanniquellie', target: 6.1, collected: 4.4, properties: 7400, compliant: 52, delinquent: 940, delinquentAmt: 3.2, trend: [2.0, 2.4, 2.9, 3.4, 3.9, 4.4], topType: 'Farmland', q1: 1.9, q2: 2.5 },
  { name: 'Lofa', capital: 'Voinjama', target: 3.8, collected: 2.4, properties: 4800, compliant: 41, delinquent: 710, delinquentAmt: 2.1, trend: [1.0, 1.2, 1.5, 1.8, 2.1, 2.4], topType: 'Farm Land (Outside Urban)', q1: 1.0, q2: 1.4 },
  { name: 'Grand Bassa', capital: 'Buchanan', target: 4.2, collected: 3.1, properties: 5200, compliant: 55, delinquent: 620, delinquentAmt: 1.9, trend: [1.4, 1.7, 2.0, 2.4, 2.7, 3.1], topType: 'Residential', q1: 1.3, q2: 1.8 },
  { name: 'Grand Cape Mount', capital: 'Robertsport', target: 2.6, collected: 1.7, properties: 3100, compliant: 44, delinquent: 480, delinquentAmt: 1.2, trend: [0.7, 0.9, 1.1, 1.3, 1.5, 1.7], topType: 'City/Town Lots', q1: 0.7, q2: 1.0 },
  { name: 'Grand Gedeh', capital: 'Zwedru', target: 2.8, collected: 1.9, properties: 3400, compliant: 46, delinquent: 510, delinquentAmt: 1.4, trend: [0.8, 1.0, 1.2, 1.4, 1.6, 1.9], topType: 'Farmland', q1: 0.8, q2: 1.1 },
  { name: 'Grand Kru', capital: 'Barclayville', target: 1.4, collected: 0.8, properties: 1800, compliant: 38, delinquent: 320, delinquentAmt: 0.7, trend: [0.3, 0.4, 0.5, 0.6, 0.7, 0.8], topType: 'Vacant Land', q1: 0.3, q2: 0.5 },
  { name: 'Maryland', capital: 'Harper', target: 2.2, collected: 1.5, properties: 2600, compliant: 47, delinquent: 390, delinquentAmt: 1.0, trend: [0.6, 0.8, 1.0, 1.1, 1.3, 1.5], topType: 'Residential', q1: 0.6, q2: 0.9 },
  { name: 'Rivercess', capital: 'Cestos City', target: 1.2, collected: 0.6, properties: 1400, compliant: 34, delinquent: 280, delinquentAmt: 0.5, trend: [0.2, 0.3, 0.4, 0.4, 0.5, 0.6], topType: 'Farmland', q1: 0.2, q2: 0.4 },
  { name: 'Sinoe', capital: 'Greenville', target: 1.8, collected: 1.1, properties: 2100, compliant: 42, delinquent: 360, delinquentAmt: 0.8, trend: [0.4, 0.5, 0.7, 0.8, 0.9, 1.1], topType: 'City/Town Lots', q1: 0.4, q2: 0.7 },
  { name: 'Gbarpolu', capital: 'Bopolu', target: 1.0, collected: 0.5, properties: 1200, compliant: 31, delinquent: 240, delinquentAmt: 0.4, trend: [0.2, 0.2, 0.3, 0.3, 0.4, 0.5], topType: 'Farmland', q1: 0.2, q2: 0.3 },
  { name: 'River Gee', capital: 'Fish Town', target: 1.1, collected: 0.6, properties: 1300, compliant: 36, delinquent: 260, delinquentAmt: 0.5, trend: [0.2, 0.3, 0.3, 0.4, 0.5, 0.6], topType: 'Farmland', q1: 0.2, q2: 0.4 },
  { name: 'Bomi', capital: 'Tubmanburg', target: 1.6, collected: 1.0, properties: 1900, compliant: 43, delinquent: 310, delinquentAmt: 0.7, trend: [0.4, 0.5, 0.6, 0.7, 0.8, 1.0], topType: 'Residential', q1: 0.4, q2: 0.6 },
];

type SortKey = 'name' | 'collected' | 'target' | 'compliant' | 'delinquent';

function getPerf(collected: number, target: number) {
  const pct = (collected / target) * 100;
  if (pct >= 90) return { label: 'On Track', color: '#4ade80', bg: 'rgba(74,222,128,0.1)' };
  if (pct >= 70) return { label: 'Moderate', color: '#fbbf24', bg: 'rgba(251,191,36,0.1)' };
  return { label: 'Below Target', color: '#f87171', bg: 'rgba(248,113,113,0.1)' };
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}

function ChartTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-xl" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}>
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <span className="font-bold">${p.value}M</span></p>
      ))}
    </div>
  );
}

export default function CountyPaymentPerformanceClient() {
  const [sortKey, setSortKey] = useState<SortKey>('collected');
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedCounty, setExpandedCounty] = useState<string | null>(null);
  const [filterPerf, setFilterPerf] = useState<'All' | 'On Track' | 'Moderate' | 'Below Target'>('All');

  const totalTarget = COUNTIES.reduce((s, c) => s + c.target, 0);
  const totalCollected = COUNTIES.reduce((s, c) => s + c.collected, 0);
  const totalDelinquent = COUNTIES.reduce((s, c) => s + c.delinquentAmt, 0);
  const avgCompliance = Math.round(COUNTIES.reduce((s, c) => s + c.compliant, 0) / COUNTIES.length);

  const sorted = [...COUNTIES]
    .filter(c => filterPerf === 'All' || getPerf(c.collected, c.target).label === filterPerf)
    .sort((a, b) => {
      const av = sortKey === 'name' ? a.name : a[sortKey];
      const bv = sortKey === 'name' ? b.name : b[sortKey];
      if (typeof av === 'string' && typeof bv === 'string') return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(a => !a);
    else { setSortKey(key); setSortAsc(false); }
  };

  const barData = COUNTIES.slice(0, 10).map(c => ({ name: c.name.slice(0, 8), target: c.target, collected: c.collected }));

  return (
    <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 2xl:px-10 max-w-screen-2xl mx-auto" style={{ backgroundColor: 'var(--dark-bg)' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link href="/" className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity" style={{ color: 'var(--dark-muted)' }}>
              <ArrowLeft size={12} />
              Dashboard
            </Link>
          </div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--dark-text)' }}>County Payment Performance</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--dark-muted)' }}>
            All 15 Liberian Counties · FY 2026 · Real Property Tax Collections
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80"
            style={{ backgroundColor: 'var(--dark-card)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}
          >
            <Download size={12} />
            Export
          </button>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Target FY2026', value: `$${totalTarget.toFixed(1)}M`, sub: '15 counties combined', color: 'var(--accent)', icon: <MapPin size={16} style={{ color: 'var(--accent)' }} /> },
          { label: 'Total Collected YTD', value: `$${totalCollected.toFixed(1)}M`, sub: `${((totalCollected/totalTarget)*100).toFixed(1)}% of target`, color: '#4ade80', icon: <TrendingUp size={16} style={{ color: '#4ade80' }} /> },
          { label: 'Avg Compliance Rate', value: `${avgCompliance}%`, sub: 'Across all counties', color: '#60a5fa', icon: <CheckCircle size={16} style={{ color: '#60a5fa' }} /> },
          { label: 'Total Delinquency', value: `$${totalDelinquent.toFixed(1)}M`, sub: 'Outstanding balances', color: '#f87171', icon: <AlertTriangle size={16} style={{ color: '#f87171' }} /> },
        ].map(kpi => (
          <div key={kpi.label} className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--dark-muted)' }}>{kpi.label}</p>
              {kpi.icon}
            </div>
            <p className="text-2xl font-bold tabular-nums" style={{ color: kpi.color }}>{kpi.value}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Bar Chart — Top 10 Counties */}
      <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--dark-text)' }}>Collections vs Target — Top 10 Counties</h3>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--dark-muted)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--dark-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}M`} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Legend wrapperStyle={{ fontSize: '11px', color: 'var(--dark-muted)' }} />
              <Bar dataKey="target" name="Target" fill="rgba(212,160,23,0.25)" radius={[3, 3, 0, 0]} />
              <Bar dataKey="collected" name="Collected" fill="#d4a017" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filter + Table */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 py-4 border-b" style={{ borderColor: 'var(--dark-border)' }}>
          <h3 className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>All 15 Counties — Detailed Performance</h3>
          <div className="flex items-center gap-2">
            <Filter size={12} style={{ color: 'var(--dark-muted)' }} />
            {(['All', 'On Track', 'Moderate', 'Below Target'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilterPerf(f)}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                style={{
                  backgroundColor: filterPerf === f ? 'var(--accent)' : 'rgba(255,255,255,0.06)',
                  color: filterPerf === f ? 'var(--accent-foreground)' : 'var(--dark-muted)',
                  border: `1px solid ${filterPerf === f ? 'var(--accent)' : 'var(--dark-border)'}`,
                }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Table header */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
                {[
                  { key: 'name', label: 'County' },
                  { key: 'collected', label: 'Collected' },
                  { key: 'target', label: 'Target' },
                  { key: null, label: 'Progress' },
                  { key: 'compliant', label: 'Compliance' },
                  { key: 'delinquent', label: 'Delinquent Accts' },
                  { key: null, label: 'Status' },
                  { key: null, label: '' },
                ].map((col, i) => (
                  <th
                    key={i}
                    className={`px-4 py-3 text-left font-semibold uppercase tracking-wider ${col.key ? 'cursor-pointer hover:opacity-80' : ''}`}
                    style={{ color: 'var(--dark-muted)', letterSpacing: '0.06em' }}
                    onClick={() => col.key && handleSort(col.key as SortKey)}
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      {col.key && sortKey === col.key && (
                        sortAsc ? <ChevronUp size={10} /> : <ChevronDown size={10} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((county, idx) => {
                const perf = getPerf(county.collected, county.target);
                const pct = Math.min((county.collected / county.target) * 100, 100);
                const isExpanded = expandedCounty === county.name;
                const trendData = county.trend.map((v, i) => ({ month: ['Jan','Feb','Mar','Apr','May','Jun'][i], value: v }));

                return (
                  <React.Fragment key={county.name}>
                    <tr
                      className="transition-colors duration-100 hover:bg-white/[0.03]"
                      style={{ borderBottom: '1px solid var(--dark-border)' }}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-semibold" style={{ color: 'var(--dark-text)' }}>{county.name}</p>
                          <p style={{ color: 'var(--dark-muted)' }}>{county.capital} · {county.properties.toLocaleString()} parcels</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-bold tabular-nums" style={{ color: '#4ade80' }}>${county.collected}M</td>
                      <td className="px-4 py-3 tabular-nums" style={{ color: 'var(--dark-muted)' }}>${county.target}M</td>
                      <td className="px-4 py-3" style={{ minWidth: 100 }}>
                        <div className="space-y-1">
                          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: perf.color }} />
                          </div>
                          <p className="text-xs tabular-nums" style={{ color: 'var(--dark-muted)' }}>{pct.toFixed(1)}%</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold" style={{ color: county.compliant >= 60 ? '#4ade80' : county.compliant >= 45 ? '#fbbf24' : '#f87171' }}>
                          {county.compliant}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-semibold" style={{ color: '#f87171' }}>{county.delinquent.toLocaleString()}</p>
                          <p style={{ color: 'var(--dark-muted)' }}>${county.delinquentAmt}M outstanding</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{ backgroundColor: perf.bg, color: perf.color }}
                        >
                          {perf.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setExpandedCounty(isExpanded ? null : county.name)}
                          className="px-2.5 py-1 rounded-lg text-xs font-medium transition-all hover:opacity-80"
                          style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}
                        >
                          {isExpanded ? 'Collapse' : 'Details'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
                        <td colSpan={8} className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Trend chart */}
                            <div className="md:col-span-2">
                              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>Monthly Collection Trend (Jan–Jun 2026)</p>
                              <div className="h-32">
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={trendData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                    <XAxis dataKey="month" tick={{ fontSize: 9, fill: 'var(--dark-muted)' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 9, fill: 'var(--dark-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}M`} />
                                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.1)' }} />
                                    <Line type="monotone" dataKey="value" name="Collected" stroke="#d4a017" strokeWidth={2} dot={{ fill: '#d4a017', r: 3 }} />
                                  </LineChart>
                                </ResponsiveContainer>
                              </div>
                            </div>

                            {/* Stats */}
                            <div className="space-y-3">
                              <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid var(--dark-border)' }}>
                                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>Property Type Breakdown</p>
                                <p className="text-xs" style={{ color: 'var(--dark-text)' }}>Top: <span style={{ color: 'var(--accent)' }}>{county.topType}</span></p>
                              </div>
                              <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid var(--dark-border)' }}>
                                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>Quarterly Performance</p>
                                <div className="flex justify-between text-xs">
                                  <span style={{ color: 'var(--dark-muted)' }}>Q1 2026</span>
                                  <span className="font-bold" style={{ color: '#60a5fa' }}>${county.q1}M</span>
                                </div>
                                <div className="flex justify-between text-xs mt-1">
                                  <span style={{ color: 'var(--dark-muted)' }}>Q2 2026</span>
                                  <span className="font-bold" style={{ color: '#4ade80' }}>${county.q2}M</span>
                                </div>
                              </div>
                              <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.2)' }}>
                                <p className="text-xs font-semibold mb-1" style={{ color: '#f87171' }}>Delinquency Alert</p>
                                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                                  {county.delinquent.toLocaleString()} accounts · ${county.delinquentAmt}M outstanding
                                </p>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
